/**
 * Política de reembolso. Texto legal: qualquer alteração deve ser revista por uma pessoa (de
 * preferência jurídica) e manter as duas línguas com o mesmo conteúdo. O Paddle (revendedor)
 * exige uma janela de reembolso de pelo menos 14 dias.
 */
import type { Locale } from '@nolens/i18n';
import { TEAM_TRIAL_DAYS } from '@nolens/domain';
import { CONTACT_EMAIL } from '../lib/contact';
import type { LegalText } from './privacy';

/** Dias para pedir o reembolso da primeira compra ou de uma renovação. */
const REFUND_DAYS = 14;

const en: LegalText = {
  title: 'Refund policy',
  updated: 'Nolens Guides, by rdoria.com. Last updated: October 26, 2026.',
  sections: [
    {
      h: 'Who processes payments',
      p: 'Our order process is conducted by our online reseller Paddle.com. Paddle.com is the Merchant of Record for all our orders: it handles the payment, the invoice, sales tax (VAT) and refunds.',
    },
    {
      h: `${REFUND_DAYS}-day money-back guarantee`,
      p: `If Pro or Pro Team isn’t right for you, you can ask for a full refund within ${REFUND_DAYS} days of the payment, without having to give a reason. This applies to the first purchase and to each renewal, monthly or annual.`,
    },
    {
      h: 'How to ask for a refund',
      li: [
        `Write to ${CONTACT_EMAIL} with the email of your account, or reply to the receipt email from Paddle. You can also request it directly at paddle.net.`,
        'The refund goes back to the payment method you used, usually within 5 to 10 business days, depending on your bank.',
        'With the refund, the plan ends: the account returns to the Free plan (or the team to its limits without a plan). Your guides are not deleted.',
      ],
    },
    {
      h: `After ${REFUND_DAYS} days`,
      p: `You can cancel at any time and keep the plan until the end of the period already paid; it won’t renew. We don’t give refunds for the unused part of a period after the first ${REFUND_DAYS} days, except when the law requires it, when the service was unavailable for a significant part of the period because of a fault on our side, when the service closes, or when you cancel because a change to the terms is to your disadvantage (see the terms of use).`,
    },
    {
      h: 'Trials and free plan',
      p: `The Free plan and the ${TEAM_TRIAL_DAYS}-day team trial (with Pro) are free and are never charged, so there is nothing to refund. A trial does not turn into a paid plan without you choosing one.`,
    },
    {
      h: 'Your rights as a consumer',
      p: 'This policy doesn’t limit the rights the law gives you as a consumer, including the right of withdrawal in the European Union.',
    },
    {
      h: 'Contact',
      p: `Questions about payments or refunds: ${CONTACT_EMAIL}.`,
    },
  ],
};

const ptPT: LegalText = {
  title: 'Política de reembolso',
  updated: 'Nolens Guides, por rdoria.com. Última atualização: 26 de outubro de 2026.',
  sections: [
    {
      h: 'Quem processa os pagamentos',
      p: 'As encomendas são processadas pelo nosso revendedor online Paddle.com. O Paddle.com é o comerciante registado (Merchant of Record) de todas as encomendas: trata do pagamento, da fatura, do IVA e dos reembolsos.',
    },
    {
      h: `Garantia de reembolso de ${REFUND_DAYS} dias`,
      p: `Se o Pro ou o Pro Equipa não forem o que precisa, pode pedir o reembolso total até ${REFUND_DAYS} dias depois do pagamento, sem ter de dar uma razão. Vale para a primeira compra e para cada renovação, mensal ou anual.`,
    },
    {
      h: 'Como pedir o reembolso',
      li: [
        `Escreva para ${CONTACT_EMAIL} com o email da sua conta, ou responda ao email do recibo do Paddle. Também o pode pedir diretamente em paddle.net.`,
        'O reembolso volta para o meio de pagamento que usou, normalmente em 5 a 10 dias úteis, conforme o banco.',
        'Com o reembolso, o plano termina: a conta volta ao plano Grátis (ou a equipa aos limites sem plano). Os seus guias não são apagados.',
      ],
    },
    {
      h: `Depois dos ${REFUND_DAYS} dias`,
      p: `Pode cancelar a qualquer momento e mantém o plano até ao fim do período já pago; não se renova. Não reembolsamos a parte não usada de um período depois dos primeiros ${REFUND_DAYS} dias, exceto quando a lei o obrigar, quando o serviço esteve indisponível durante uma parte relevante do período por uma falha nossa, quando o serviço fechar, ou quando cancelar porque uma alteração aos termos o prejudica (ver os termos de utilização).`,
    },
    {
      h: 'Testes e plano Grátis',
      p: `O plano Grátis e os ${TEAM_TRIAL_DAYS} dias de teste das equipas (com o Pro) são gratuitos e nunca são cobrados, por isso não há nada a reembolsar. Um teste não passa a plano pago sem que o escolha.`,
    },
    {
      h: 'Os seus direitos como consumidor',
      p: 'Esta política não limita os direitos que a lei lhe dá como consumidor, incluindo o direito de livre resolução na União Europeia.',
    },
    {
      h: 'Contacto',
      p: `Dúvidas sobre pagamentos ou reembolsos: ${CONTACT_EMAIL}.`,
    },
  ],
};

const REFUNDS: Record<Locale, LegalText> = { en, 'pt-PT': ptPT };
export const refundsText = (l: Locale): LegalText => REFUNDS[l];
